import { List } from "@mui/material";
import { SidebarItemProps } from "@components/sidebar/sidebar-items";
import { useAppSelector } from "@store/store-helper";
import { SidebarEvents } from "@utils/track-event/track-event-list";
import { appStateSelector } from "@store/app/app-selector";
import { SidebarListItem } from "@components/sidebar/sidebar-list-item";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { SidebarSkeleton } from "@components/common/skeleton-loading/sidebar-skeleton";

interface Props {
  /** Flag whether the sidebar should be shown full size or minified */
  isSidebarFullSize: boolean;

  /** A list of items to show in the sidebar. */
  sidebarItems: SidebarItemProps[];
}

/**
 * Map over the list of items and return them as a List component
 * Also handles selection and the selected item in the sidebar
 */
export function SidebarList({
  isSidebarFullSize,
  sidebarItems,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { isBetaTestingEnabled, isAlphaTestingEnabled } =
    useAppSelector(appStateSelector);
  const { hasFeatureCompanyLevel } = useHasUserValidFeatureCompanyLevel();
  const { fetchSelectedCompanyContext } = useAppSelector(
    fetchingSdbCompanyFlagsSelector
  );

  const filteredSidebarItems = sidebarItems.filter((sidebarItem) => {
    // Don't show a sidebar item if the item is in beta testing and beta testing is not enabled
    if (sidebarItem.shouldShowOnlyInBeta && !isBetaTestingEnabled) {
      return false;
    }

    // Don't show a sidebar item if the item is in alpha testing and alpha testing is not enabled
    if (sidebarItem.shouldShowOnlyInAlpha && !isAlphaTestingEnabled) {
      return false;
    }

    // Only show the items that the user has access to.
    return (
      sidebarItem.requiredFeatureCompanyLevel &&
      hasFeatureCompanyLevel({
        featureName: sidebarItem.requiredFeatureCompanyLevel,
      })
    );
  });

  // Show skeleton if fetching the selected company context is in progress
  if (fetchSelectedCompanyContext === "pending") {
    return <SidebarSkeleton />;
  }

  return (
    <List>
      {filteredSidebarItems.map((sidebarItem) => (
        <SidebarListItem
          key={sidebarItem.identity}
          isSidebarFullSize={isSidebarFullSize}
          sidebarItem={sidebarItem}
          onClick={() =>
            trackEvent({
              name: SidebarEvents.openTab,
              props: { tab: sidebarItem.identity.toLowerCase() },
            })
          }
        />
      ))}
    </List>
  );
}
